import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function Login() {
  const { darkMode } = useDarkMode();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = 'E-Mail ist erforderlich';
    if (!password) errs.password = 'Passwort ist erforderlich';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError('');
    try {
      await login(email, password);
      navigate(redirectTo);
    } catch (err: any) {
      setApiError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) => {
    const hasError = errors[field];
    const border = hasError
      ? 'border-red-500'
      : darkMode
        ? 'border-slate-600'
        : 'border-slate-300';
    return `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${border} ${
      darkMode ? 'bg-slate-700 text-white placeholder-slate-400' : 'bg-white text-slate-900 placeholder-slate-400'
    }`;
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        darkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}
    >
      <div
        className={`w-full max-w-md rounded-2xl border shadow-lg p-8 ${
          darkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
        }`}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-3">
            <Logo size="lg" />
          </div>
          <h1
            className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            Willkommen zurück
          </h1>
          <p
            className={`mt-1 text-sm ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Melden Sie sich an
          </p>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              E-Mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.email;
                  return next;
                });
                setApiError('');
              }}
              placeholder="max@beispiel.de"
              autoComplete="email"
              className={inputClass('email')}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.password;
                    return next;
                  });
                  setApiError('');
                }}
                placeholder="Ihr Passwort"
                autoComplete="current-password"
                className={inputClass('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Forgot Password */}
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
            >
              Passwort vergessen?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <span>{isSubmitting ? 'Wird angemeldet...' : 'Anmelden'}</span>
          </button>
        </form>

        {/* Register Link */}
        <p
          className={`mt-6 text-center text-sm ${
            darkMode ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          Noch kein Konto?{' '}
          <Link
            to="/register"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Registrieren
          </Link>
        </p>
      </div>
    </div>
  );
}
